import type {
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';

export interface FieldProps {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | undefined;
  readonly children: (props: {
    id: string;
    'aria-invalid': boolean;
    'aria-describedby': string | undefined;
  }) => ReactNode;
}

/**
 * Label, hint and error wired to the control by id.
 *
 * The render-prop shape means the accessibility plumbing cannot be forgotten:
 * the only way to get a control inside a field is to accept the props it hands
 * you.
 */
export function Field({ label, hint, error, children }: FieldProps): ReactElement {
  const id = useId();
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, 'aria-invalid': error !== undefined, 'aria-describedby': describedBy })}
      {hint !== undefined ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error !== undefined ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>): ReactElement {
  return <input {...props} className={`input ${props.className ?? ''}`.trim()} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): ReactElement {
  return <select {...props} className={`select ${props.className ?? ''}`.trim()} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): ReactElement {
  return <textarea {...props} className={`textarea ${props.className ?? ''}`.trim()} />;
}
